import Carregando from '../components/Carregando';
import { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Button, TextField, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Grid, IconButton,
  InputAdornment, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableHead, TableRow,
  Avatar, Tab, Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ListIcon from '@mui/icons-material/List';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import AcessoNegado from '../components/AcessoNegado';
import EmDesenvolvimento from '../components/EmDesenvolvimento';
import dayjs from 'dayjs';

const prioridadeConfig = {
  baixa: { label: 'Baixa', color: '#639922', bg: '#EAF3DE' },
  media: { label: 'Média', color: '#BA7517', bg: '#FAEEDA' },
  alta:  { label: 'Alta',  color: '#E24B4A', bg: '#FCEBEB' },
};

const statusAtivConfig = {
  pendente:  { label: 'Pendente',  color: 'warning', icon: <PendingIcon fontSize="small" /> },
  realizado: { label: 'Realizado', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  cancelado: { label: 'Cancelado', color: 'error',   icon: <CancelIcon fontSize="small" /> },
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function CardOportunidade({ op, onEdit, onMover, onAtividade, etapas }) {
  const prio = prioridadeConfig[op.prioridade] || prioridadeConfig.media;
  const pendentes = op.atividades?.filter((a) => a.status === 'pendente').length || 0;

  return (
    <Card sx={{
      mb: 1, borderLeft: `3px solid ${prio.color}`,
      '&:hover': { boxShadow: 2 }, transition: 'box-shadow 0.2s',
    }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="subtitle2" fontWeight={600} fontSize={12} flex={1} lineHeight={1.3}>
            {op.titulo}
          </Typography>
          <Box display="flex">
            <IconButton size="small" onClick={() => onAtividade(op)} sx={{ p: 0.3 }}>
              <AddCommentIcon sx={{ fontSize: 13, color: '#888' }} />
            </IconButton>
            <IconButton size="small" onClick={() => onEdit(op)} sx={{ p: 0.3 }}>
              <EditIcon sx={{ fontSize: 13, color: '#888' }} />
            </IconButton>
          </Box>
        </Box>

        {op.empresa && (
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            {op.empresa}
          </Typography>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
          {op.valor_estimado ? (
            <Typography variant="caption" fontWeight={700} color="#1a1a2e">
              R$ {Number(op.valor_estimado).toLocaleString('pt-BR')}
            </Typography>
          ) : <span />}
          <Box sx={{
            fontSize: 10, px: 0.8, py: 0.2, borderRadius: 99,
            backgroundColor: prio.bg, color: prio.color, fontWeight: 600,
          }}>
            {op.probabilidade}%
          </Box>
        </Box>

        {op.responsavel_nome && (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <Avatar sx={{ width: 14, height: 14, fontSize: 8, bgcolor: '#1a1a2e' }}>
              {op.responsavel_nome.charAt(0)}
            </Avatar>
            <Typography variant="caption" color="text.secondary" fontSize={10}>
              {op.responsavel_nome}
            </Typography>
          </Box>
        )}

        {op.data_prevista_fechamento && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <CalendarMonthIcon sx={{ fontSize: 11, color: '#888' }} />
            <Typography variant="caption" color="text.secondary" fontSize={10}>
              {dayjs(op.data_prevista_fechamento).format('DD/MM/YY')}
            </Typography>
          </Box>
        )}

        {pendentes > 0 && (
          <Box mt={0.5} sx={{ borderTop: '0.5px solid #f0f0f0', pt: 0.5 }}>
            <Typography variant="caption" color="warning.main" fontSize={10} fontWeight={600}>
              {pendentes} atividade{pendentes > 1 ? 's' : ''} pendente{pendentes > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        <TextField select size="small" fullWidth value={op.etapa}
          onChange={(e) => onMover(op.id, e.target.value)}
          sx={{ mt: 1, '& .MuiInputBase-input': { fontSize: 10, py: 0.4 } }}>
          {etapas.map((et) => (
            <MenuItem key={et.id} value={et.id} sx={{ fontSize: 11 }}>{et.nome}</MenuItem>
          ))}
        </TextField>
      </CardContent>
    </Card>
  );
}

export default function CRM() {
  const { isGestor, isComercial, moduloAtivo, getPrevisao } = useAuth();
  const moduloLiberado = moduloAtivo('crm');
  const podeVer = isGestor || isComercial;

  const [etapas, setEtapas] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [tiposAtividade, setTiposAtividade] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [view, setView] = useState('kanban');
  const [busca, setBusca] = useState('');
  const [filtroPrioridade, setFiltroPrioridade] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [abrirModal, setAbrirModal] = useState(false);
  const [abrirAtividade, setAbrirAtividade] = useState(false);
  const [opEditando, setOpEditando] = useState(null);
  const [opAtividade, setOpAtividade] = useState(null);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');

  const [form, setForm] = useState({
    titulo: '', empresa: '', etapa: '',
    valor_estimado: '', probabilidade: 0, prioridade: 'media',
    data_prevista_fechamento: '', contato_nome: '',
    contato_email: '', contato_telefone: '',
    descricao: '', observacoes: '',
  });

  const [formAtividade, setFormAtividade] = useState({
    tipo: '', assunto: '', descricao: '',
    data_vencimento: dayjs().format('YYYY-MM-DDTHH:mm'),
    contato_nome: '', contato_email: '',
    contato_telefone: '', organizacao: '',
    atribuido_a: '', status: 'pendente',
  });

  const carregarDados = () => {
    Promise.all([
      api.get('/crm/etapas/'),
      api.get('/crm/atividades/'),
      api.get('/crm/tipos-atividade/'),
    ]).then(([et, at, ti]) => {
      setEtapas(et.data);
      setAtividades(at.data);
      setTiposAtividade(ti.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!podeVer || !moduloLiberado) { setLoading(false); return; }
    carregarDados();
  }, [podeVer, moduloLiberado]);

  const todasOportunidades = useMemo(() =>
    etapas.flatMap((et) => et.oportunidades.map((op) => ({ ...op, etapa_nome: et.nome }))),
    [etapas]
  );

  const responsaveis = useMemo(() => {
    const nomes = [...new Set(todasOportunidades.map((op) => op.responsavel_nome).filter(Boolean))];
    return nomes;
  }, [todasOportunidades]);

  const filtrar = (ops) => ops.filter((op) => {
    const matchBusca = !busca ||
      op.titulo?.toLowerCase().includes(busca.toLowerCase()) ||
      op.empresa?.toLowerCase().includes(busca.toLowerCase());
    const matchPrio = !filtroPrioridade || op.prioridade === filtroPrioridade;
    const matchResp = !filtroResponsavel || op.responsavel_nome === filtroResponsavel;
    return matchBusca && matchPrio && matchResp;
  });

  const etapasFiltradas = etapas.map((et) => ({
    ...et, oportunidades: filtrar(et.oportunidades),
  }));

  const totalPipeline = todasOportunidades.reduce((acc, op) => acc + Number(op.valor_estimado || 0), 0);
  const projecao = todasOportunidades.reduce((acc, op) =>
    acc + (Number(op.valor_estimado || 0) * (op.probabilidade / 100)), 0);
  const fechadas = etapas.find((et) => et.nome === 'Projeto Fechado')?.oportunidades.length || 0;
  const taxa = todasOportunidades.length > 0 ? Math.round((fechadas / todasOportunidades.length) * 100) : 0;

  const atividadesFiltradas = atividades.filter((a) => {
    const matchStatus = !filtroStatus || a.status === filtroStatus;
    const matchBusca = !busca ||
      a.assunto?.toLowerCase().includes(busca.toLowerCase()) ||
      a.oportunidade_titulo?.toLowerCase().includes(busca.toLowerCase());
    return matchStatus && matchBusca;
  });

  const handleAbrirModal = (op = null) => {
    if (op) {
      setOpEditando(op);
      setForm({
        titulo: op.titulo, empresa: op.empresa || '',
        etapa: op.etapa, valor_estimado: op.valor_estimado || '',
        probabilidade: op.probabilidade || 0, prioridade: op.prioridade || 'media',
        data_prevista_fechamento: op.data_prevista_fechamento || '',
        contato_nome: op.contato_nome || '', contato_email: op.contato_email || '',
        contato_telefone: op.contato_telefone || '', descricao: op.descricao || '',
        observacoes: op.observacoes || '',
      });
    } else {
      setOpEditando(null);
      setForm({
        titulo: '', empresa: '', etapa: etapas[0]?.id || '',
        valor_estimado: '', probabilidade: 0, prioridade: 'media',
        data_prevista_fechamento: '', contato_nome: '',
        contato_email: '', contato_telefone: '', descricao: '', observacoes: '',
      });
    }
    setAbrirModal(true);
  };

  const handleSalvar = async () => {
    setErro('');
    try {
      if (opEditando) {
        await api.patch(`/crm/oportunidades/${opEditando.id}/`, form);
        setSucesso('Oportunidade atualizada!');
      } else {
        await api.post('/crm/oportunidades/', form);
        setSucesso('Oportunidade criada!');
      }
      setAbrirModal(false);
      carregarDados();
    } catch { setErro('Erro ao salvar oportunidade.'); }
  };

  const handleMover = async (opId, novaEtapaId) => {
    try {
      await api.patch(`/crm/oportunidades/${opId}/`, { etapa: novaEtapaId });
      carregarDados();
    } catch { setErro('Erro ao mover oportunidade.'); }
  };

  const handleAbrirAtividade = (op = null) => {
    setOpAtividade(op);
    setFormAtividade({
      tipo: tiposAtividade[0]?.id || '',
      assunto: '', descricao: '',
      data_vencimento: dayjs().format('YYYY-MM-DDTHH:mm'),
      contato_nome: op?.contato_nome || '',
      contato_email: op?.contato_email || '',
      contato_telefone: op?.contato_telefone || '',
      organizacao: op?.empresa || '',
      atribuido_a: '', status: 'pendente',
      oportunidade: op?.id || '',
    });
    setAbrirAtividade(true);
  };

  const handleSalvarAtividade = async () => {
    setErro('');
    try {
      await api.post('/crm/atividades/', formAtividade);
      setSucesso('Atividade registrada!');
      setAbrirAtividade(false);
      carregarDados();
    } catch { setErro('Erro ao registrar atividade.'); }
  };

  const handleMarcarRealizado = async (id) => {
    try {
      await api.patch(`/crm/atividades/${id}/`, { status: 'realizado' });
      setSucesso('Atividade marcada como realizada!');
      carregarDados();
    } catch { setErro('Erro ao atualizar atividade.'); }
  };

  if (!moduloLiberado) {
    return <EmDesenvolvimento previsao={getPrevisao('crm')} />;
  }

  if (!podeVer) {
    return <AcessoNegado />;
  }

  return (
    <Box>
      <PageHeader
        titulo="CRM"
        acoes={(
          <>
            <Button variant="outlined" startIcon={<AddIcon />}
              onClick={() => handleAbrirAtividade()}
              sx={{ borderColor: '#1a1a2e', color: '#1a1a2e' }}>
              Nova atividade
            </Button>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => handleAbrirModal()}
              sx={{ backgroundColor: '#1a1a2e' }}>
              Nova oportunidade
            </Button>
          </>
        )}
      />

      {/* STATS */}
      <Grid container spacing={1.5} mb={2}>
        {[
          { label: 'Total em pipeline', val: `R$ ${totalPipeline.toLocaleString('pt-BR')}` },
          { label: 'Projeção de fechamento', val: `R$ ${Math.round(projecao).toLocaleString('pt-BR')}` },
          { label: 'Oportunidades abertas', val: todasOportunidades.length },
          { label: 'Taxa de conversão', val: `${taxa}%` },
        ].map((s) => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card sx={{ border: '0.5px solid #e0e0e0' }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" fontSize={10} textTransform="uppercase">
                  {s.label}
                </Typography>
                <Typography variant="h6" fontWeight={500} fontSize={18}>{s.val}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tab label="Pipeline" />
        <Tab label={`Atividades (${atividades.filter((a) => a.status === 'pendente').length} pendentes)`} />
      </Tabs>

      {/* ABA PIPELINE */}
      <TabPanel value={tab} index={0}>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Buscar oportunidade ou empresa..."
            value={busca} onChange={(e) => setBusca(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
          <TextField select size="small" value={filtroPrioridade}
            onChange={(e) => setFiltroPrioridade(e.target.value)}
            sx={{ minWidth: 130 }} label="Prioridade">
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="baixa">Baixa</MenuItem>
            <MenuItem value="media">Média</MenuItem>
            <MenuItem value="alta">Alta</MenuItem>
          </TextField>
          <TextField select size="small" value={filtroResponsavel}
            onChange={(e) => setFiltroResponsavel(e.target.value)}
            sx={{ minWidth: 150 }} label="Responsável">
            <MenuItem value="">Todos</MenuItem>
            {responsaveis.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup value={view} exclusive
            onChange={(e, v) => v && setView(v)} size="small">
            <ToggleButton value="kanban"><ViewKanbanIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="lista"><ListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Carregando />
        ) : view === 'kanban' ? (
          <Box sx={{ overflowX: 'auto', pb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, minWidth: etapasFiltradas.length * 215 }}>
              {etapasFiltradas.map((et) => (
                <Box key={et.id} sx={{ width: 205, flexShrink: 0 }}>
                  <Box sx={{
                    backgroundColor: '#1a1a2e', borderRadius: '6px 6px 0 0',
                    px: 1.5, py: 1,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      fontSize={11}
                      noWrap
                      sx={{ maxWidth: 140, color: '#ffffff' }}
                    >
                      {et.nome}
                    </Typography>
                    <Box sx={{
                      backgroundColor: 'rgba(255,255,255,0.15)', color: 'white',
                      fontSize: 10, px: 0.8, py: 0.2, borderRadius: 99, flexShrink: 0,
                    }}>
                      {et.oportunidades.length}
                    </Box>
                  </Box>
                  {et.total_valor > 0 && (
                    <Box sx={{ backgroundColor: 'rgba(26,26,46,0.06)', px: 1.5, py: 0.4 }}>
                      <Typography variant="caption" color="text.secondary" fontSize={10}>
                        R$ {Number(et.total_valor).toLocaleString('pt-BR')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{
                    backgroundColor: '#f8f9fa',
                    border: '0.5px solid #e0e0e0', borderTop: 'none',
                    borderRadius: '0 0 6px 6px', p: 1, minHeight: 200,
                  }}>
                    {et.oportunidades.length === 0 ? (
                      <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', textAlign: 'center', mt: 3, opacity: 0.5, fontSize: 11 }}>
                        Sem oportunidades
                      </Typography>
                    ) : (
                      et.oportunidades.map((op) => (
                        <CardOportunidade key={op.id} op={op} etapas={etapas}
                          onEdit={handleAbrirModal} onMover={handleMover}
                          onAtividade={handleAbrirAtividade} />
                      ))
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Card>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Oportunidade</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Etapa</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell align="center">Prob.</TableCell>
                  <TableCell align="center">Prioridade</TableCell>
                  <TableCell align="center">Fechamento</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrar(todasOportunidades).map((op) => {
                  const prio = prioridadeConfig[op.prioridade] || prioridadeConfig.media;
                  return (
                    <TableRow key={op.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{op.titulo}</Typography>
                      </TableCell>
                      <TableCell>{op.empresa || '—'}</TableCell>
                      <TableCell><Typography variant="caption">{op.etapa_nome}</Typography></TableCell>
                      <TableCell>{op.responsavel_nome || '—'}</TableCell>
                      <TableCell align="right">
                        {op.valor_estimado ? `R$ ${Number(op.valor_estimado).toLocaleString('pt-BR')}` : '—'}
                      </TableCell>
                      <TableCell align="center">{op.probabilidade}%</TableCell>
                      <TableCell align="center">
                        <Chip label={prio.label} size="small"
                          sx={{ backgroundColor: prio.bg, color: prio.color, fontWeight: 600, fontSize: 10 }} />
                      </TableCell>
                      <TableCell align="center">
                        {op.data_prevista_fechamento ? dayjs(op.data_prevista_fechamento).format('DD/MM/YY') : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton size="small" onClick={() => handleAbrirAtividade(op)}>
                            <AddCommentIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleAbrirModal(op)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </TabPanel>

      {/* ABA ATIVIDADES */}
      <TabPanel value={tab} index={1}>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Buscar atividade..."
            value={busca} onChange={(e) => setBusca(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
          <TextField select size="small" value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            sx={{ minWidth: 150 }} label="Status">
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="pendente">Pendente</MenuItem>
            <MenuItem value="realizado">Realizado</MenuItem>
            <MenuItem value="cancelado">Cancelado</MenuItem>
          </TextField>
        </Box>

        {atividadesFiltradas.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary" textAlign="center">
              Nenhuma atividade encontrada.
            </Typography>
          </CardContent></Card>
        ) : (
          <Card>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Assunto</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Negócio</TableCell>
                  <TableCell>Contato</TableCell>
                  <TableCell>Organização</TableCell>
                  <TableCell align="center">Vencimento</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Atribuído a</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {atividadesFiltradas.map((a) => {
                  const st = statusAtivConfig[a.status] || statusAtivConfig.pendente;
                  const vencido = a.status === 'pendente' && dayjs(a.data_vencimento).isBefore(dayjs());
                  return (
                    <TableRow key={a.id} hover sx={{ backgroundColor: vencido ? '#fff8f8' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{a.assunto}</Typography>
                      </TableCell>
                      <TableCell>{a.tipo_nome || '—'}</TableCell>
                      <TableCell>{a.oportunidade_titulo || '—'}</TableCell>
                      <TableCell>{a.contato_nome || '—'}</TableCell>
                      <TableCell>{a.organizacao || '—'}</TableCell>
                      <TableCell align="center">
                        <Typography variant="caption"
                          color={vencido ? 'error' : 'text.secondary'}
                          fontWeight={vencido ? 700 : 400}>
                          {dayjs(a.data_vencimento).format('DD/MM/YY HH:mm')}
                          {vencido && ' ⚠️'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={st.label} color={st.color} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        {a.atribuido_a_nome || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {a.status === 'pendente' && (
                          <IconButton size="small" color="success"
                            onClick={() => handleMarcarRealizado(a.id)}
                            title="Marcar como realizado">
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </TabPanel>

      {/* MODAL OPORTUNIDADE */}
      <Dialog open={abrirModal} onClose={() => setAbrirModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>{opEditando ? 'Editar oportunidade' : 'Nova oportunidade'}</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Título da oportunidade"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Empresa"
                value={form.empresa}
                onChange={(e) => setForm({ ...form, empresa: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Etapa"
                value={form.etapa}
                onChange={(e) => setForm({ ...form, etapa: e.target.value })}>
                {etapas.map((et) => (
                  <MenuItem key={et.id} value={et.id}>{et.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Valor estimado (R$)" type="number"
                value={form.valor_estimado}
                onChange={(e) => setForm({ ...form, valor_estimado: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Probabilidade (%)" type="number"
                value={form.probabilidade}
                onChange={(e) => setForm({ ...form, probabilidade: e.target.value })}
                inputProps={{ min: 0, max: 100 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select label="Prioridade"
                value={form.prioridade}
                onChange={(e) => setForm({ ...form, prioridade: e.target.value })}>
                <MenuItem value="baixa">Baixa</MenuItem>
                <MenuItem value="media">Média</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Previsão de fechamento" type="date"
                value={form.data_prevista_fechamento}
                onChange={(e) => setForm({ ...form, data_prevista_fechamento: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nome do contato"
                value={form.contato_nome}
                onChange={(e) => setForm({ ...form, contato_nome: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email do contato"
                value={form.contato_email}
                onChange={(e) => setForm({ ...form, contato_email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefone do contato"
                value={form.contato_telefone}
                onChange={(e) => setForm({ ...form, contato_telefone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrição" multiline rows={2}
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observações" multiline rows={2}
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvar} sx={{ backgroundColor: '#1a1a2e' }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL ATIVIDADE */}
      <Dialog open={abrirAtividade} onClose={() => setAbrirAtividade(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {opAtividade ? `Nova atividade — ${opAtividade.titulo}` : 'Nova atividade'}
        </DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Assunto"
                value={formAtividade.assunto}
                onChange={(e) => setFormAtividade({ ...formAtividade, assunto: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Tipo de atividade"
                value={formAtividade.tipo}
                onChange={(e) => setFormAtividade({ ...formAtividade, tipo: e.target.value })}>
                {tiposAtividade.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Negócio (oportunidade)"
                value={formAtividade.oportunidade || ''}
                onChange={(e) => setFormAtividade({ ...formAtividade, oportunidade: e.target.value })}>
                {todasOportunidades.map((op) => (
                  <MenuItem key={op.id} value={op.id}>{op.titulo}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Data de vencimento" type="datetime-local"
                value={formAtividade.data_vencimento}
                onChange={(e) => setFormAtividade({ ...formAtividade, data_vencimento: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Pessoa de contato"
                value={formAtividade.contato_nome}
                onChange={(e) => setFormAtividade({ ...formAtividade, contato_nome: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email"
                value={formAtividade.contato_email}
                onChange={(e) => setFormAtividade({ ...formAtividade, contato_email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefone"
                value={formAtividade.contato_telefone}
                onChange={(e) => setFormAtividade({ ...formAtividade, contato_telefone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Organização"
                value={formAtividade.organizacao}
                onChange={(e) => setFormAtividade({ ...formAtividade, organizacao: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrição" multiline rows={2}
                value={formAtividade.descricao}
                onChange={(e) => setFormAtividade({ ...formAtividade, descricao: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirAtividade(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvarAtividade}
            sx={{ backgroundColor: '#1a1a2e' }}>
            Registrar atividade
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}